import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import PageHeader from '@/components/PageHeader';
import { ErrorBanner } from '@/components/ui';
import { templatesService, type SearchTemplate } from '@/api/services/templates';
import { useMe } from '@/context/MeContext';
import { useToast } from '@/components/Toast';
import { formatApiError } from '@/utils/formatApiError';
import {
  canExportTemplates,
  canFavoriteTemplates,
  canInstallTemplates,
} from '@/utils/navCapabilities';

function downloadTemplateJson(title: string, data: Record<string, unknown>) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${title.replace(/\s+/g, '-').toLowerCase() || 'template'}.json`;
  anchor.click();
  URL.revokeObjectURL(url);
}

export default function TemplateDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { me } = useMe();
  const { toast } = useToast();
  const canInstall = canInstallTemplates(me);
  const canFavorite = canFavoriteTemplates(me);
  const canExport = canExportTemplates(me);
  const [template, setTemplate] = useState<SearchTemplate | null>(null);
  const [versions, setVersions] = useState<Record<string, unknown>[]>([]);
  const [compare, setCompare] = useState<Record<string, unknown>[] | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([templatesService.get(id), templatesService.versions(id)])
      .then(async ([tpl, ver]) => {
        setTemplate(tpl);
        setVersions(ver.items);
        if (tpl.updateAvailable) {
          const cmp = await templatesService.compare(
            id,
            tpl.updateAvailable.installedVersion,
            tpl.updateAvailable.latestVersion,
          );
          setCompare(cmp.changes);
        }
      })
      .catch((e) => setError(formatApiError(e, 'Failed to load template')))
      .finally(() => setLoading(false));
  }, [id]);

  async function toggleFavorite() {
    if (!template || !id) return;
    try {
      if (template.favorited) await templatesService.unfavorite(id);
      else await templatesService.favorite(id);
      setTemplate({ ...template, favorited: !template.favorited });
    } catch (e) {
      setError(formatApiError(e, 'Favorite action failed'));
    }
  }

  async function handleExport() {
    if (!template || !id) return;
    try {
      const data = await templatesService.export(id);
      downloadTemplateJson(template.title, data);
      toast('Template exported', 'success');
    } catch (e) {
      setError(formatApiError(e, 'Export failed'));
    }
  }

  async function handleDuplicate() {
    if (!id) return;
    try {
      await templatesService.duplicate(id);
      toast('Template duplicated to My Templates', 'success');
      navigate('/templates/my', { state: { refresh: Date.now() } });
    } catch (e) {
      setError(formatApiError(e, 'Duplicate failed'));
    }
  }

  if (loading) return <p className="muted">Loading template…</p>;
  if (!template) return <p className="muted">Template not found.</p>;

  const preview = template.preview;

  return (
    <>
      <PageHeader title={template.title} subtitle={template.shortDescription}>
        <Link to="/templates" className="btn btn-ghost btn-sm">
          Back
        </Link>
        {canFavorite ? (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => void toggleFavorite()}>
            {template.favorited ? 'Unfavorite' : 'Favorite'}
          </button>
        ) : null}
        {canInstall ? (
          <>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => void handleDuplicate()}>
              Duplicate
            </button>
            <Link to={`/templates/install?id=${id}`} className="btn btn-sm">
              Install
            </Link>
          </>
        ) : null}
        {canExport ? (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => void handleExport()}>
            Export
          </button>
        ) : null}
      </PageHeader>

      {error ? <ErrorBanner message={error} onDismiss={() => setError('')} /> : null}

      {template.updateAvailable ? (
        <div className="alert alert-info">
          Update available: v{template.updateAvailable.installedVersion} → v
          {template.updateAvailable.latestVersion}. Installed searches are never auto-updated.
        </div>
      ) : null}

      <div className="template-detail-grid">
        <section className="card card-elevated">
          <h2>Overview</h2>
          <p>{template.longDescription || template.shortDescription}</p>
          <dl className="detail-grid">
            <dt>Category</dt>
            <dd>{template.category}</dd>
            <dt>Version</dt>
            <dd>{template.version}</dd>
            <dt>Author</dt>
            <dd>{template.author}</dd>
            <dt>Installs</dt>
            <dd>{template.installCount ?? 0}</dd>
            <dt>Complexity</dt>
            <dd>{preview?.complexity || 'medium'}</dd>
          </dl>
        </section>

        <section className="card card-elevated">
          <h2>Configuration preview</h2>
          <dl className="detail-grid">
            <dt>Marketplaces</dt>
            <dd>{(preview?.marketplaces || template.marketplaces || []).join(', ') || '—'}</dd>
            <dt>Schedule</dt>
            <dd>
              {preview?.schedule && typeof preview.schedule === 'object'
                ? JSON.stringify(preview.schedule)
                : '—'}
            </dd>
            <dt>Keywords</dt>
            <dd>{String(preview?.keywords || preview?.filters?.search_text || '—')}</dd>
          </dl>
          {preview?.filters ? (
            <>
              <h3>Filters</h3>
              <pre className="template-config-preview">{JSON.stringify(preview.filters, null, 2)}</pre>
            </>
          ) : null}
        </section>

        <section className="card card-elevated">
          <h2>Version history</h2>
          <ul className="template-version-list">
            {versions.map((v) => (
              <li key={String(v.id)}>
                <strong>v{String(v.version)}</strong>
                <span className="muted"> — {String(v.changelog || 'No changelog')}</span>
              </li>
            ))}
          </ul>
          {compare && compare.length > 0 ? (
            <>
              <h3>Changes in latest version</h3>
              <ul>
                {compare.map((c, i) => (
                  <li key={i}>
                    {String(c.field)} changed
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </section>
      </div>
    </>
  );
}
